import { useCallback, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from '@/components/ui/dialog';
import i18n from '@/utils/i18n';

interface SendcloudResetResult {
  confirmed: boolean;
  reset: boolean;
}

interface SendcloudResetDialogProps {
  open: boolean;
  reset: boolean;
  onResetChange: (value: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

function SendcloudResetDialog({
  open,
  reset,
  onResetChange,
  onConfirm,
  onCancel,
}: SendcloudResetDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{i18n.t('Order:resetSendcloudTitle')}</DialogTitle>
          <DialogDescription>
            {i18n.t('Order:resetSendcloudDescription')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <label className="tw:flex tw:items-start tw:gap-2">
            <Checkbox
              className="tw:mt-0.5"
              checked={reset}
              onCheckedChange={(checked) => onResetChange(!!checked)}
            />
            <span className="tw:flex tw:flex-col tw:gap-1">
              <span className="tw:text-sm tw:font-medium">
                {i18n.t('Order:resetSendcloudCheckbox')}
              </span>
              <span className="tw:text-sm tw:text-muted-foreground">
                {i18n.t('Order:resetSendcloudCostWarning')}
              </span>
            </span>
          </label>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{i18n.t('Common:cancel')}</Button>
          </DialogClose>
          <Button onClick={onConfirm}>{i18n.t('Common:save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Asks the user whether the order's SendCloud state should be wiped when
 * saving an ERROR → APPROVED transition, so the backend re-announces a fresh
 * SendCloud order from the corrected data.
 */
export function useSendcloudResetGuard() {
  const [open, setOpen] = useState(false);
  const [reset, setReset] = useState(false);
  const resolveRef = useRef<
    ((result: SendcloudResetResult) => void) | undefined
  >(undefined);

  const requestSendcloudReset = useCallback(() => {
    setReset(false);
    setOpen(true);
    return new Promise<SendcloudResetResult>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolveRef.current?.({ confirmed: true, reset });
    setOpen(false);
  }, [reset]);

  const handleCancel = useCallback(() => {
    resolveRef.current?.({ confirmed: false, reset: false });
    setOpen(false);
  }, []);

  const guardDialog = (
    <SendcloudResetDialog
      open={open}
      reset={reset}
      onResetChange={setReset}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { requestSendcloudReset, SendcloudResetDialog: guardDialog };
}
